import { Component, type ReactNode } from "react";
import { Alert, Button, Container, Stack, Text, Title } from "@mantine/core";

import { t } from "@/i18n";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Catches render/lifecycle errors anywhere in the tree
 * and shows a themed fallback with a reload, instead of React unmounting to a
 * blank page. Must be a class component (React's only error-boundary API).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    // Surface for local debugging; a real telemetry sink is a v1.1 concern.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.error !== null) {
      return (
        <Container size="sm" py="xl">
          <Stack gap="md">
            <Title order={2}>{t("error.title")}</Title>
            <Text c="dimmed">{t("error.subtitle")}</Text>
            <Alert color="red" title={t("error.detail_title")}>
              {this.state.error.message}
            </Alert>
            <Button onClick={this.handleReload} maw={200}>
              {t("error.reload")}
            </Button>
          </Stack>
        </Container>
      );
    }
    return this.props.children;
  }
}
